/**
 * Custom hook for managing user access data in the Access Manager panel.
 *
 * Handles fetching users from the API, search/scope filtering, and
 * optimistic updates with rollback on API failure.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { listUsers } from "./accessManagerApi";

/**
 * @typedef {Object} UserRecord
 * @property {string} email
 * @property {string[]} scopes
 * @property {string|null} granted_by
 */

/**
 * Hook for fetching, filtering, and optimistically updating user access data.
 *
 * @param {Object} options
 * @param {string} [options.searchQuery=""] - Filter users by email substring (case-insensitive).
 * @param {string} [options.scopeFilter=""] - Filter users to only those with this scope granted.
 * @returns {{
 *   users: UserRecord[],
 *   loading: boolean,
 *   error: string|null,
 *   retry: () => void,
 *   optimisticUpdate: (email: string, newScopes: string[]) => void,
 *   rollback: (email: string, previousScopes: string[]) => void
 * }}
 */
export function useAccessUsers({ searchQuery = "", scopeFilter = "" } = {}) {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listUsers();
      setAllUsers(response.data);
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || "Failed to fetch users";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /**
   * Immediately updates a user's scopes in local state before API confirmation.
   */
  const optimisticUpdate = useCallback((email, newScopes) => {
    setAllUsers((prev) =>
      prev.map((user) =>
        user.email === email ? { ...user, scopes: newScopes } : user
      )
    );
  }, []);

  /**
   * Reverts a user's scopes to a previous state after an API failure.
   */
  const rollback = useCallback((email, previousScopes) => {
    setAllUsers((prev) =>
      prev.map((user) =>
        user.email === email ? { ...user, scopes: previousScopes } : user
      )
    );
  }, []);

  /**
   * Filtered users based on search query and scope filter.
   */
  const users = useMemo(() => {
    let filtered = allUsers;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((user) =>
        user.email.toLowerCase().includes(query)
      );
    }

    if (scopeFilter) {
      filtered = filtered.filter((user) =>
        user.scopes.includes(scopeFilter)
      );
    }

    return filtered;
  }, [allUsers, searchQuery, scopeFilter]);

  return {
    users,
    loading,
    error,
    retry: fetchUsers,
    optimisticUpdate,
    rollback,
  };
}

export default useAccessUsers;
